import type { ErrorResponse, RefreshResponse } from "@pulsar-anvil-studios/api-types";
import type { Route } from "@pulsar-anvil-studios/routes";
import { AUTH_REFRESH, resolvePath } from "@pulsar-anvil-studios/routes/auth";
import ky, { isHTTPError } from "ky";

const isErrorResponse = (data: unknown): data is ErrorResponse =>
  typeof data === "object" &&
  data !== null &&
  "error" in data &&
  typeof (data as ErrorResponse).error?.message === "string";

let refreshPromise: Promise<RefreshResponse> | null = null;

/** Strip leading slash from route path for ky's prefixUrl compatibility. */
export function apiPath(route: Route, params?: Record<string, string>): string {
  return resolvePath(route, params).slice(1);
}

/** POST /auth/refresh using raw ky (no 401 retry) to rotate HttpOnly cookies. */
export const refreshSession = (): Promise<RefreshResponse> => {
  if (!refreshPromise) {
    refreshPromise = ky
      .post(apiPath(AUTH_REFRESH), {
        prefix: import.meta.env.VITE_API_URL,
        credentials: "include",
        retry: 0,
      })
      .json<RefreshResponse>()
      .then((response) => {
        refreshPromise = null;
        return response;
      })
      .catch((error: unknown) => {
        refreshPromise = null;
        throw error;
      });
  }
  return refreshPromise;
};

export const api = ky.create({
  prefix: import.meta.env.VITE_API_URL,
  credentials: "include",
  retry: { limit: 1, statusCodes: [401] },
  totalTimeout: 30_000,
  hooks: {
    beforeError: [
      ({ error }) => {
        if (isHTTPError(error) && isErrorResponse(error.data)) {
          error.message = error.data.error.message;
        }
        return error;
      },
    ],
    beforeRetry: [
      async () => {
        try {
          await refreshSession();
        } catch {
          // Full reload intentional — clears all client-side state on session expiry
          window.location.href = "/login";
          throw new Error("session-expired");
        }
      },
    ],
  },
});
